/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _CustomArrayNodeBase_instances, _CustomArrayNodeBase_generationNumber, _CustomArrayNodeBase_mapTreesFromFieldData;
import { oob } from "@fluidframework/core-utils/internal";
import { EmptyKey } from "../core/index.js";
import { getOrCreateMapTreeNode, getSchemaAndPolicy, isFlexTreeNode, isMapTreeSequenceField, } from "../feature-libraries/index.js";
import { getOrCreateNodeFromFlexTreeNode, prepareContentForHydration, } from "./proxies.js";
import { getOrCreateInnerNode } from "./proxyBinding.js";
import { 
// eslint-disable-next-line import/no-deprecated
typeNameSymbol, NodeKind, typeSchemaSymbol, } from "./core/index.js";
import { mapTreeFromNodeData } from "./toMapTree.js";
import { fail } from "../util/index.js";
import { getFlexSchema } from "./toFlexSchema.js";
import { UsageError } from "@fluidframework/telemetry-utils/internal";
import { getKernel } from "./core/index.js";
import { TreeNodeValid } from "./treeNodeValid.js";
/**
 * A {@link TreeNode} which implements 'readonly T[]' and the array mutation APIs.
 * @public
 */
export const TreeArrayNode = {
    /**
     * Wrap an iterable of items to inserted as consecutive items in a array.
     * @remarks
     * The object returned by this function can be inserted into a {@link (TreeArrayNode:interface)}.
     * Its contents will be inserted consecutively in the corresponding location in the array.
     * @example
     * ```ts
     * array.insertAtEnd(TreeArrayNode.spread(iterable))
     * ```
     */
    spread: (content) => create(content),
};
/**
 * Package internal construction API.
 * Use {@link (TreeArrayNode:variable).spread} to create an instance of this type instead.
 */
let create;
/**
 * Used to insert iterable content into a {@link (TreeArrayNode:interface)}.
 * Use {@link (TreeArrayNode:variable).spread} to create an instance of this type.
 * @sealed @public
 */
export class IterableTreeArrayContent {
    constructor(content) {
        this.content = content;
    }
    /**
     * Iterates over content for nodes to insert.
     */
    [Symbol.iterator]() {
        return this.content[Symbol.iterator]();
    }
}
(() => {
    create = (content) => new IterableTreeArrayContent(content);
})();
/**
 * Given a array node proxy, returns its underlying LazySequence field.
 */
function getSequenceField(arrayNode) {
    return getOrCreateInnerNode(arrayNode).getBoxed(EmptyKey);
}
// For compatibility, we are initially implement 'readonly T[]' by applying the Array.prototype methods
// to the array node proxy.  Over time, we should replace these with efficient implementations on LazySequence
// to avoid re-entering the proxy as these methods access 'length' and the indexed properties.
//
// For brevity, the current implementation dynamically builds a property descriptor map from a list of
// Array functions we want to re-expose via the proxy.
const arrayPrototypeKeys = [
    "concat",
    "entries",
    "every",
    "filter",
    "find",
    "findIndex",
    "flat",
    "flatMap",
    "forEach",
    "includes",
    "indexOf",
    "join",
    "keys",
    "lastIndexOf",
    "map",
    "reduce",
    "reduceRight",
    "slice",
    "some",
    "toLocaleString",
    "toString",
    // "copyWithin",
    // "fill",
    // "length",
    // "pop",
    // "push",
    // "reverse",
    // "shift",
    // "sort",
    // "splice",
    // "unshift",
];
/**
 * {@link TreeNodeValid}, but modified to add members from Array.prototype named in {@link arrayPrototypeKeys}.
 * @privateRemarks
 * Since a lot of scratch types and values are involved with creating this,
 * it's generating using an immediately invoked function expression (IIFE).
 * This is a common JavaScript pattern for cases like this to avoid cluttering the scope.
 */
const TreeNodeWithArrayFeatures = (() => {
    /**
     * {@link TreeNodeValid}, but modified to add members from Array.prototype named in {@link arrayPrototypeKeys}.
     */
    class TreeNodeWithArrayFeaturesUntyped extends TreeNodeValid {
    }
    // Modify TreeNodeWithArrayFeaturesUntyped to add the members from Array.prototype
    arrayPrototypeKeys.forEach((key) => {
        Object.defineProperty(TreeNodeWithArrayFeaturesUntyped.prototype, key, {
            value: Array.prototype[key],
        });
    });
    return TreeNodeWithArrayFeaturesUntyped;
})();
/* eslint-enable @typescript-eslint/explicit-member-accessibility, @typescript-eslint/no-explicit-any */
/**
 * Attempts to coerce the given property key to an integer index property.
 * @param key - The property key to coerce.
 * @param exclusiveMax - This restricts the range in which the resulting index is allowed to be.
 * The coerced index of `key` must be less than `exclusiveMax` or else this function will return `undefined`.
 * This is useful for reading an array within the bounds of its length, e.g. `asIndex(key, array.length)`.
 */
export function asIndex(key, exclusiveMax) {
    if (typeof key !== "string") {
        return undefined;
    }
    // TODO: It may be worth a '0' <= ch <= '9' check before calling 'Number' to quickly
    // reject 'length' as an index, or even parsing integers ourselves.
    const asNumber = Number(key);
    if (!Number.isInteger(asNumber)) {
        return undefined;
    }
    // Check that the original string is the same after converting to a number and back again.
    // This prevents keys like "5.0", "0x5", " 5" from coercing to 5, and keys like " " or "" from coercing to 0.
    const asString = String(asNumber);
    if (asString !== key) {
        return undefined;
    }
    // TODO: See 'matrix/range.ts' for fast integer coercing + range check.
    return 0 <= asNumber && asNumber < exclusiveMax ? asNumber : undefined;
}
/**
 * @param allowAdditionalProperties - If true, setting of unexpected properties will be forwarded to the target object.
 * Otherwise setting of unexpected properties will error.
 * @param proxyTarget - Target object of the proxy. Must provide an own `length` value property
 * (which is not used but must exist for getOwnPropertyDescriptor invariants) and the array functionality from {@link arrayNodePrototype}.
 * Controls the prototype exposed by the produced proxy.
 * @param dispatchTarget - provides the functionally of the node, implementing all fields.
 */
function createArrayNodeProxy(allowAdditionalProperties, proxyTarget, dispatchTarget) {
    // To satisfy 'deepEquals' level scrutiny, the target of the proxy must be an array literal in order
    // to pass 'Object.getPrototypeOf'.  It also satisfies 'Array.isArray' and 'Object.prototype.toString'
    // requirements without use of Array[Symbol.species], which is potentially on a path ot deprecation.
    const proxy = new Proxy(proxyTarget, {
        get: (target, key, receiver) => {
            const field = getSequenceField(receiver);
            const maybeIndex = asIndex(key, field.length);
            if (maybeIndex === undefined) {
                if (key === "length") {
                    return field.length;
                }
                // Pass the proxy as the receiver here, so that any methods on
                // the prototype receive `proxy` as `this`.
                return Reflect.get(dispatchTarget, key, receiver);
            }
            const maybeContent = field.at(maybeIndex);
            return isFlexTreeNode(maybeContent)
                ? getOrCreateNodeFromFlexTreeNode(maybeContent)
                : maybeContent;
        },
        set: (target, key, newValue, receiver) => {
            if (key === "length") {
                // To allow "length" to look like "length" on an array, getOwnPropertyDescriptor has to report it as a writable value.
                // This means the proxy target must provide a length value, but since it can't use getters and setters, it can't be correct.
                // Therefor length has to be handled in this proxy.
                // Since it's not actually mutable, return false so setting it will produce a type error.
                return false;
            }
            // 'Symbol.isConcatSpreadable' may be set on an Array instance to modify the behavior of
            // the concat method.  We allow this property to be added to the dispatch object.
            if (key === Symbol.isConcatSpreadable) {
                return Reflect.set(dispatchTarget, key, newValue, receiver);
            }
            // Array nodes treat all non-negative integer indexes as array access.
            // Using Infinity here (rather than length) ensures that indexing past the end doesn't create additional session local properties.
            const maybeIndex = asIndex(key, Number.POSITIVE_INFINITY);
            if (maybeIndex !== undefined) {
                // For MVP, we otherwise disallow setting properties (mutation is only available via the array node mutation APIs).
                // To ensure a clear and actionable error experience, we will throw explicitly here, rather than just returning false.
                throw new UsageError("Cannot set indexed properties on array nodes. Use array node mutation APIs to alter the array.");
            }
            return allowAdditionalProperties ? Reflect.set(target, key, newValue, receiver) : false;
        },
        has: (target, key) => {
            const field = getSequenceField(proxy);
            const maybeIndex = asIndex(key, field.length);
            return maybeIndex !== undefined || Reflect.has(dispatchTarget, key);
        },
        ownKeys: (target) => {
            const field = getSequenceField(proxy);
            // TODO: Would a lazy iterator to produce the indexes work / be more efficient?
            // TODO: Need to surface 'Symbol.isConcatSpreadable' as an own key.
            const keys = Array.from({ length: field.length }, (_, index) => `${index}`);
            if (allowAdditionalProperties) {
                keys.push(...Reflect.ownKeys(target));
            }
            else {
                keys.push("length");
            }
            return keys;
        },
        getOwnPropertyDescriptor: (target, key) => {
            const field = getSequenceField(proxy);
            const maybeIndex = asIndex(key, field.length);
            if (maybeIndex !== undefined) {
                const val = field.at(maybeIndex);
                // To satisfy 'deepEquals' level scrutiny, the property descriptor for indexed properties must
                // be a simple value property (as opposed to using getter) and declared writable/enumerable/configurable.
                return {
                    value: isFlexTreeNode(val) ? getOrCreateNodeFromFlexTreeNode(val) : val,
                    writable: true, // For MVP, setting indexed properties is reported as allowed here (for deep equals compatibility noted above), but not actually supported.
                    enumerable: true,
                    configurable: true,
                };
            }
            else if (key === "length") {
                // To satisfy 'deepEquals' level scrutiny, the property descriptor for 'length' must be a simple
                // value property (as opposed to using getter) and be declared writable / non-configurable.
                return {
                    value: field.length,
                    writable: true,
                    enumerable: false,
                    configurable: false,
                };
            }
            return Reflect.getOwnPropertyDescriptor(dispatchTarget, key);
        },
        defineProperty(target, key, attributes) {
            const maybeIndex = asIndex(key, Number.POSITIVE_INFINITY);
            if (maybeIndex !== undefined) {
                throw new UsageError("Shadowing of array indices is not permitted.");
            }
            return Reflect.defineProperty(dispatchTarget, key, attributes);
        },
    });
    return proxy;
}
class CustomArrayNodeBase extends TreeNodeWithArrayFeatures {
    constructor(input) {
        super(input);
        _CustomArrayNodeBase_instances.add(this);
        /**
         * Generation number which is incremented any time we have an edit on the node.
         * Used during iteration to make sure there has been no edits that were concurrently made.
         */
        _CustomArrayNodeBase_generationNumber.set(this, 0);
        getKernel(this).on("nodeChanged", () => {
            __classPrivateFieldSet(this, _CustomArrayNodeBase_generationNumber, __classPrivateFieldGet(this, _CustomArrayNodeBase_generationNumber, "f") + 1, "f");
        });
    }
    toJSON() {
        // This override causes the class instance to `JSON.stringify` as `[a, b]` rather than `{0: a, 1: b}`.
        return Array.from(this);
    }
    // Instances of this class are used as the dispatch object for the proxy,
    // and thus its set of keys is used to implement `has` (for the `in` operator) for the non-numeric cases.
    // Therefore it must include `length`,
    // even though this "length" is never invoked (due to being shadowed by the proxy provided own property).
    get length() {
        return fail("Proxy should intercept length");
    }
    [(_CustomArrayNodeBase_generationNumber = new WeakMap(), _CustomArrayNodeBase_instances = new WeakSet(), _CustomArrayNodeBase_mapTreesFromFieldData = function _CustomArrayNodeBase_mapTreesFromFieldData(value) {
        const sequenceField = getSequenceField(this);
        const content = value;
        const mapTrees = content
            .flatMap((c) => c instanceof IterableTreeArrayContent ? Array.from(c) : [c])
            .map((c) => mapTreeFromNodeData(c, this.simpleSchema, sequenceField.context?.nodeKeyManager, getSchemaAndPolicy(sequenceField)));
        if (sequenceField.context !== undefined) {
            prepareContentForHydration(mapTrees, sequenceField.context.checkout.forest);
        }
        return mapTrees;
    }, Symbol.iterator)]() {
        return this.values();
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get [Symbol.unscopables]() {
        // This might not be the exact right set of values, but it only matters for `with` clauses which are deprecated and are banned in strict mode, so it shouldn't matter much.
        // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/with for details.
        return Array.prototype[Symbol.unscopables];
    }
    at(index) {
        const field = getSequenceField(this);
        const val = field.boxedAt(index);
        if (val === undefined) {
            return val;
        }
        return getOrCreateNodeFromFlexTreeNode(val);
    }
    insertAt(index, ...value) {
        const field = getSequenceField(this);
        validateIndex(index, field, "insertAt", true);
        const content = __classPrivateFieldGet(this, _CustomArrayNodeBase_instances, "m", _CustomArrayNodeBase_mapTreesFromFieldData).call(this, value);
        field.editor.insert(index, content);
    }
    insertAtStart(...value) {
        this.insertAt(0, ...value);
    }
    insertAtEnd(...value) {
        this.insertAt(this.length, ...value);
    }
    removeAt(index) {
        const field = getSequenceField(this);
        validateIndex(index, field, "removeAt");
        field.editor.remove(index, 1);
    }
    removeRange(start, end) {
        const field = getSequenceField(this);
        const { length } = field;
        const removeStart = start ?? 0;
        const removeEnd = Math.min(length, end ?? length);
        validatePositiveIndex(removeStart);
        validatePositiveIndex(removeEnd);
        if (removeEnd < removeStart) {
            // This catches both the case where start is > array.length and when start is > end.
            throw new UsageError('Too large of "start" value passed to TreeArrayNode.removeRange.');
        }
        field.editor.remove(removeStart, removeEnd - removeStart);
    }
    moveToStart(sourceIndex, source) {
        const sourceArray = source ?? this;
        const sourceField = getSequenceField(sourceArray);
        validateIndex(sourceIndex, sourceField, "moveToStart");
        this.moveRangeToIndex(0, sourceIndex, sourceIndex + 1, source);
    }
    moveToEnd(sourceIndex, source) {
        const sourceArray = source ?? this;
        const sourceField = getSequenceField(sourceArray);
        validateIndex(sourceIndex, sourceField, "moveToEnd");
        this.moveRangeToIndex(this.length, sourceIndex, sourceIndex + 1, source);
    }
    moveToIndex(destinationIndex, sourceIndex, source) {
        const sourceArray = source ?? this;
        const sourceField = getSequenceField(sourceArray);
        const destinationField = getSequenceField(this);
        validateIndex(destinationIndex, destinationField, "moveToIndex", true);
        validateIndex(sourceIndex, sourceField, "moveToIndex");
        this.moveRangeToIndex(destinationIndex, sourceIndex, sourceIndex + 1, source);
    }
    moveRangeToStart(sourceStart, sourceEnd, source) {
        validateIndexRange(sourceStart, sourceEnd, source ?? getSequenceField(this), "moveRangeToStart");
        this.moveRangeToIndex(0, sourceStart, sourceEnd, source);
    }
    moveRangeToEnd(sourceStart, sourceEnd, source) {
        validateIndexRange(sourceStart, sourceEnd, source ?? getSequenceField(this), "moveRangeToEnd");
        this.moveRangeToIndex(this.length, sourceStart, sourceEnd, source);
    }
    moveRangeToIndex(destinationIndex, sourceStart, sourceEnd, source) {
        const destinationField = getSequenceField(this);
        const sourceField = source !== undefined ? getSequenceField(source) : destinationField;
        validateIndex(destinationIndex, destinationField, "moveRangeToIndex", true);
        validateIndexRange(sourceStart, sourceEnd, source ?? destinationField, "moveRangeToIndex");
        // TODO: determine support for move across different sequence types
        if (destinationField.schema.types !== undefined && sourceField !== destinationField) {
            for (let i = sourceStart; i < sourceEnd; i++) {
                const sourceNode = sourceField.boxedAt(i) ?? oob();
                if (!destinationField.schema.types.has(sourceNode.schema.name)) {
                    throw new UsageError("Type in source sequence is not allowed in destination.");
                }
            }
        }
        const movedCount = sourceEnd - sourceStart;
        if (destinationField.context === undefined) {
            if (!isMapTreeSequenceField(sourceField)) {
                throw new UsageError("Cannot move elements from an inserted array to an array that has not yet been inserted");
            }
            if (sourceField !== destinationField || destinationIndex < sourceStart) {
                destinationField.editor.insert(destinationIndex, sourceField.editor.remove(sourceStart, movedCount));
            }
            else if (destinationIndex > sourceStart + movedCount) {
                destinationField.editor.insert(destinationIndex - movedCount, sourceField.editor.remove(sourceStart, movedCount));
            }
        }
        else {
            if (sourceField.context === undefined) {
                throw new UsageError("Cannot move elements from an array that has not yet been inserted to an inserted array");
            }
            destinationField.context.checkout.editor.move(sourceField.getFieldPath(), sourceStart, movedCount, destinationField.getFieldPath(), destinationIndex);
        }
    }
    values() {
        return this.generateValues(__classPrivateFieldGet(this, _CustomArrayNodeBase_generationNumber, "f"));
    }
    *generateValues(initialLastUpdatedStamp) {
        if (initialLastUpdatedStamp !== __classPrivateFieldGet(this, _CustomArrayNodeBase_generationNumber, "f")) {
            throw new UsageError(`Concurrent editing and iteration is not allowed.`);
        }
        for (let i = 0; i < this.length; i++) {
            yield this.at(i) ?? fail("Index is out of bounds");
            if (initialLastUpdatedStamp !== __classPrivateFieldGet(this, _CustomArrayNodeBase_generationNumber, "f")) {
                throw new UsageError(`Concurrent editing and iteration is not allowed.`);
            }
        }
    }
}
CustomArrayNodeBase.kind = NodeKind.Array;
/**
 * Define a {@link TreeNodeSchema} for a {@link (TreeArrayNode:interface)}.
 *
 * @param name - Unique identifier for this schema including the factory's scope.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function arraySchema(identifier, info, implicitlyConstructable, customizable) {
    let flexSchema;
    // This class returns a proxy from its constructor to handle numeric indexing.
    // Alternatively it could extend a normal class which gets tons of numeric properties added.
    class schema extends CustomArrayNodeBase {
        static prepareInstance(instance, flexNode) {
            const proxyTarget = customizable ? instance : [];
            if (customizable) {
                // Since proxy reports this as a "non-configurable" property, it must exist on the underlying object used as the proxy target, not as an inherited property.
                // This should not get used as the proxy should intercept all use.
                Object.defineProperty(instance, "length", {
                    value: NaN,
                    writable: true,
                    enumerable: false,
                    configurable: false,
                });
            }
            return createArrayNodeProxy(customizable, proxyTarget, instance);
        }
        static buildRawNode(instance, input) {
            return getOrCreateMapTreeNode(flexSchema, mapTreeFromNodeData(input, this));
        }
        static oneTimeSetup() {
            flexSchema = getFlexSchema(this);
            // First run, do extra validation.
            // TODO: provide a way for TreeConfiguration to trigger this same validation to ensure it gets run early.
            // Scan for shadowing inherited members which won't work, but stop scan early to allow shadowing built in (which seems to work ok).
            {
                let prototype = this.prototype;
                // There isn't a clear cleaner way to author this loop.
                while (prototype !== schema.prototype) {
                    // Search prototype keys and check for positive integers. Throw if any are found.
                    // Shadowing of index properties on array nodes is not supported.
                    for (const key of Object.getOwnPropertyNames(prototype)) {
                        const maybeIndex = asIndex(key, Number.POSITIVE_INFINITY);
                        if (maybeIndex !== undefined) {
                            throw new UsageError(`Schema ${identifier} defines an inherited index property "${key.toString()}" which shadows a possible array index. Shadowing of array indices is not permitted.`);
                        }
                    }
                    // Since this stops at the array node base schema, it should never see a null prototype, so this case is safe.
                    // Additionally, if the prototype chain is ever messed up such that the array base schema is not in it,
                    // the null that would show up here does at least ensure this code throws instead of hanging.
                    prototype = Reflect.getPrototypeOf(prototype);
                }
            }
        }
        // eslint-disable-next-line import/no-deprecated
        get [typeNameSymbol]() {
            return identifier;
        }
        get [typeSchemaSymbol]() {
            return schema.constructorCached?.constructor;
        }
        get simpleSchema() {
            return info;
        }
    }
    schema.constructorCached = undefined;
    schema.identifier = identifier;
    schema.info = info;
    schema.implicitlyConstructable = implicitlyConstructable;
    const output = schema;
    return output;
}
function validateSafeInteger(index) {
    if (!Number.isSafeInteger(index)) {
        throw new UsageError(`Expected a safe integer, got ${index}.`);
    }
}
function validatePositiveIndex(index) {
    validateSafeInteger(index);
    if (index < 0) {
        throw new UsageError(`Expected non-negative index, got ${index}.`);
    }
}
function validateIndex(index, array, methodName, allowOnePastEnd = false) {
    validatePositiveIndex(index);
    if (allowOnePastEnd) {
        if (index > array.length) {
            throw new UsageError(`Index value passed to TreeArrayNode.${methodName} is out of bounds.`);
        }
    }
    else {
        if (index >= array.length) {
            throw new UsageError(`Index value passed to TreeArrayNode.${methodName} is out of bounds.`);
        }
    }
}
function validateIndexRange(startIndex, endIndex, array, methodName) {
    validateIndex(startIndex, array, methodName, true);
    validateIndex(endIndex, array, methodName, true);
    if (startIndex > endIndex || array.length < endIndex) {
        throw new UsageError(`Index value passed to TreeArrayNode.${methodName} is out of bounds.`);
    }
}
//# sourceMappingURL=arrayNode.js.map